import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Chip,
  Tooltip,
  Box
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningIcon from '@mui/icons-material/Warning'
import ErrorIcon from '@mui/icons-material/Error'
import { DCItemDiscount } from '../../types/dc.types'
import LandingCostBadge from './LandingCostBadge'

interface ItemMappingTableProps {
  items: DCItemDiscount[]
  onSearchClick: (index: number) => void
  onPtrChange: (index: number, ptr: number) => void
}

export default function ItemMappingTable({
  items,
  onSearchClick,
  onPtrChange
}: ItemMappingTableProps) {
  const getMatchIcon = (item: DCItemDiscount) => {
    if (!item.is_matched) {
      return (
        <Tooltip title="Not matched">
          <ErrorIcon color="error" fontSize="small" />
        </Tooltip>
      )
    }

    if ((item.match_confidence || 0) >= 0.9) {
      return (
        <Tooltip title={`${((item.match_confidence || 0) * 100).toFixed(0)}% confidence`}>
          <CheckCircleIcon color="success" fontSize="small" />
        </Tooltip>
      )
    }

    return (
      <Tooltip title={`${((item.match_confidence || 0) * 100).toFixed(0)}% confidence`}>
        <WarningIcon color="warning" fontSize="small" />
      </Tooltip>
    )
  }

  const getMatchMethodChip = (method: string | null) => {
    if (!method) return null

    const colors: Record<string, 'success' | 'primary' | 'secondary' | 'default'> = {
      exact: 'success',
      fuzzy: 'primary',
      manual: 'secondary',
      ai: 'default'
    }

    return (
      <Chip
        size="small"
        label={method}
        color={colors[method] || 'default'}
        variant="outlined"
      />
    )
  }

  return (
    <TableContainer sx={{ maxHeight: 600 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell width="4%">#</TableCell>
            <TableCell width="22%">Raw Product Name</TableCell>
            <TableCell width="18%">Matched Medicine</TableCell>
            <TableCell width="7%">Match</TableCell>
            <TableCell width="6%">Pack</TableCell>
            <TableCell width="7%">Discount</TableCell>
            <TableCell width="7%">Offer</TableCell>
            <TableCell width="7%">PTR</TableCell>
            <TableCell width="9%">Landing Cost</TableCell>
            <TableCell width="9%">Offer L.C.</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, index) => (
            <TableRow
              key={index}
              sx={{
                backgroundColor: item.is_matched ? 'inherit' : 'error.50',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <TableCell>{index + 1}</TableCell>

              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  <IconButton
                    size="small"
                    onClick={() => onSearchClick(index)}
                    title="Search for item"
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                  <span style={{ fontSize: '0.875rem' }}>
                    {item.raw_product_name}
                  </span>
                </Box>
              </TableCell>

              <TableCell>
                {item.is_matched ? (
                  <Tooltip title={item.matched_medicine_name || ''}>
                    <span style={{ fontSize: '0.875rem' }}>
                      {(item.matched_medicine_name || '').substring(0, 30)}
                      {(item.matched_medicine_name || '').length > 30 ? '...' : ''}
                    </span>
                  </Tooltip>
                ) : (
                  <span style={{ color: '#999', fontStyle: 'italic' }}>Not matched</span>
                )}
              </TableCell>

              <TableCell>
                <Box display="flex" alignItems="center" gap={0.5}>
                  {getMatchIcon(item)}
                  {getMatchMethodChip(item.match_method || null)}
                </Box>
              </TableCell>

              <TableCell>{item.raw_pack_size || '-'}</TableCell>

              <TableCell>
                {item.discount_percentage ? (
                  <Chip
                    size="small"
                    label={`${item.discount_percentage}%`}
                    color="primary"
                  />
                ) : item.raw_discount_text || '-'}
              </TableCell>

              <TableCell>
                {item.buy_qty && item.free_qty ? (
                  <Chip
                    size="small"
                    label={`${item.buy_qty}+${item.free_qty}`}
                    color="secondary"
                  />
                ) : item.raw_offer_text || '-'}
              </TableCell>

              <TableCell>
                <TextField
                  size="small"
                  type="number"
                  value={item.ptr || ''}
                  onChange={(e) => onPtrChange(index, parseFloat(e.target.value) || 0)}
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{ width: 80 }}
                  placeholder="PTR"
                />
                {item.ptr_source && (
                  <Chip
                    size="small"
                    label={item.ptr_source}
                    variant="outlined"
                    sx={{ ml: 0.5, height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </TableCell>

              <TableCell>
                <LandingCostBadge
                  landingCost={item.landing_cost}
                  offerLandingCost={item.offer_landing_cost}
                  effectiveDiscount={item.effective_discount}
                />
              </TableCell>

              <TableCell>
                {item.buy_qty > 0 && item.free_qty > 0 && item.offer_landing_cost != null ? (
                  <LandingCostBadge
                    landingCost={item.offer_landing_cost}
                    effectiveDiscount={item.effective_discount}
                  />
                ) : (
                  <span style={{ color: '#999' }}>-</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
